import { autoinject, transient } from "aurelia-dependency-injection";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { AttachmentHelper } from "../../../helpers/attachment-helper";
import { AttachmentsHelper } from "../../../utilities/attachments-helper";
import { RightShipInspectionClientModel, RightShipInspectionStatusEnum } from "../../../models/rightship-inspections/rightship-inspections.models";
import * as moment from "moment";
import { AddEntityAttachmentsRequest } from "../../../models/attachment/attachment.models";
import { Guid } from "../../../utilities/guid";
import { FileHelper } from "../../../helpers/file-helper";
import { NotificationClientService, NotificationType } from "../../../services/basics/notification-client.service";
import { AttachmentClientService } from "../../../services/attachment/attachments-client.service";
import { I18N } from "aurelia-i18n";
import * as Enumerable from "linq";
import { RightshipQiMessages } from "../../../configuration/rightshipqi-event-messages.constants";
import { Reflection } from "../../../utilities/reflection";
import { computedFrom } from "aurelia-binding";
import { RightshipEntityAttachmentManager } from "../../pages/modules/common-steps/rightship-entity-attachment/rightship-entity-attachment-manager";
import { RightshipEntityAttachmentDialog } from "../../pages/modules/common-steps/dialogs/entity-attachment/rightship-entity-attachment-dialog";
import { RightShipInspectionsService } from "../../../services/rightship-inspections/rightship-inspections.service";

//Load styles
require("../../../styles/upload-documents/core-upload-documents.scss");

@autoinject()
@transient()
export class LayoutUploadDocuments {

    private _uploadedAttachments: AddEntityAttachmentsRequest[] = [];

    public showValidationError: { [id: number]: boolean } = {
        12: false,
        13: false,
        14: false,
        15: false
    };
    public attachmentRequests: AddEntityAttachmentsRequest[] = [];
    public showDatePicker: boolean = false;
    public paste: EventListenerOrEventListenerObject;
    public isSubmitted: boolean;
    public checkIfAtleastOneIsValid: boolean = true;

    constructor(private lookupClientService: LookupClientService, private dialogService: DialogClientService, private notificationService: NotificationClientService,
        private eventAggregator: EventAggregator, private attachmentDialogBroker: RightshipEntityAttachmentManager, private i18N: I18N,
        private attachmentClientService: AttachmentClientService, private attachmentsDialog: RightshipEntityAttachmentDialog,
        private rightShipInspectionService: RightShipInspectionsService) {
        this.paste = (e: any) => {
            if (!e.propagationStopped) {
                const items = ((e.clipboardData || (e.originalEvent || {}).clipboardData) || {}).items || [];
                for (const item of items) {
                    if (item.kind === 'file') {
                        const blob = item.getAsFile();
                        const reader = new FileReader();
                        reader.onload = (f) => {
                            eventAggregator.publish(RightshipQiMessages.paste, { source: (<any>f.target).result, fileName: blob.name });
                        };
                        reader.readAsDataURL(blob);
                    }
                }
            }
        };

    }

    rightShipInspection: RightShipInspectionClientModel;

    async activate() {
        let guid = "";
        const params = window.location.pathname.split("/").filter(x => x != null && x != "");
        if(params.length > 1)
        {
            guid = params[1];
        }
        this.rightShipInspection = await this.rightShipInspectionService.getRightShipInspectionClientModelFromGuid(guid);
        this.eventAggregator.subscribe('show-date-picker', () => {
            this.showDatePicker = true;
        });

        this.eventAggregator.subscribe('hide-date-picker', () => {
            this.showDatePicker = false;
        });
        await this.lookupClientService.clearAllLookups();
    }

    @computedFrom(Reflection.getPropertyPath<LayoutUploadDocuments>(z => z.rightShipInspection.rightShipInspectionStatusId))
    public get linkClosed() {
        const statues = [
            RightShipInspectionStatusEnum.InProgress,
            RightShipInspectionStatusEnum.Scheduled,
            RightShipInspectionStatusEnum.ReviewRequired,
            RightShipInspectionStatusEnum.AwaitingResponse
        ];
        return this.rightShipInspection && statues.indexOf(this.rightShipInspection.rightShipInspectionStatusId) == -1;
    }

    @computedFrom(Reflection.getPropertyPath<LayoutUploadDocuments>(z => z.rightShipInspection),
        Reflection.getPropertyPath<LayoutUploadDocuments>(z => z.rightShipInspection.vesselDateOfBuild))
    public get age() {
        if (this.rightShipInspection) {
            if (this.rightShipInspection.vesselDateOfBuild) {
                return (Math.floor(moment().diff(this.rightShipInspection.vesselDateOfBuild, 'year', true) * 10) / 10).toFixed(1) + "y/o"
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    public openAttachmentsDialog(attachmentTypeId: number) {
        this.attachmentDialogBroker.rootItem = {
            entityType: EntityTypes.RightShipInspection,
            identifier: Guid.generateGuid()
        };
        this.attachmentDialogBroker.attachmentTypeId = attachmentTypeId;
        this.attachmentDialogBroker.rootItem["id"] = this.rightShipInspection.rightShipInspectionId;
        this.attachmentDialogBroker.saveFunc = () => {
            this.cloneFiles(attachmentTypeId);
            return new Promise<() => void>((resolve, reject) => {
                reject();
                this.attachmentsDialog.close();
            });
        };
        this.dialogService.show(this.attachmentsDialog, this.attachmentDialogBroker, true, false, false, 'right');
    }

    cloneFiles(attachmentTypeId: number) {
        const files = this.attachmentsDialog.selectedFiles.length > 0 ? this.attachmentsDialog.selectedFiles : this.attachmentsDialog.dataUri.map(x => this.attachmentsDialog.dataURLtoBlob(x));
        let i = 0;
        for (const file of files) {
            const name = this.attachmentsDialog.name[i];
            this.attachmentRequests.push({
                entityAttachmentId: null,
                physicalDocumentReceived: false,
                entityIds: [this.rightShipInspection.rightShipInspectionId],
                entityType: EntityTypes.RightShipInspection,
                file: file,
                expiryDate: null,
                gdprExpiryDate: null,
                name: name,
                attachmentTags: [],
                attachmentTypeId: attachmentTypeId,
                metaJson: "",
                guid: Guid.generateGuid(),
                expiredDocumentUpdatedDate: null
            });
            i++;
        }
    }

    open(item: AddEntityAttachmentsRequest) {
        const url = window.URL.createObjectURL(item.file);
        FileHelper.downloadFile(url, item.name);
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
        });
    }

    remove(event, item: AddEntityAttachmentsRequest) {
        event.stopPropagation();
        const index = this.attachmentRequests.findIndex(x => x.guid == item.guid);
        this.attachmentRequests.splice(index, 1);
    }

    getFilesize(url: string) {
        const fullUrl = AttachmentsHelper.getUrl(url);
        return AttachmentHelper.getFilesize(fullUrl);
    }

    attached() {
        document.addEventListener("click", this.documentClick);
        document.addEventListener("paste", this.paste);
    }

    detached() {
        document.removeEventListener("click", this.documentClick);
        document.removeEventListener("paste", this.paste);
    }

    async submit() {
        try {
            const ps = [];
            const attachmentsToUpload = this.attachmentRequests.filter(c => this._uploadedAttachments.findIndex(a => a == c) == -1);

            const acceptableExcelMimeTypes = [
                "application/zip",
                "application/x-zip-compressed",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "application/msword"
            ];
           
            const acceptablePdfMimeTypes = [
                "application/pdf"
            ];

            const acceptableImageMimeTypes = [
                "image/png",
                "image/jpeg",
            ]

            const acceptableMimeTypes = acceptableExcelMimeTypes.concat(acceptablePdfMimeTypes).concat(acceptableImageMimeTypes);

            const invalidFiles = attachmentsToUpload.filter(c => acceptableMimeTypes.indexOf(c.file.type) == -1);

            if (invalidFiles.length > 0) {

                const fileNames = Enumerable.from(invalidFiles.map(c => c.name)).distinct().toArray();

                const message = (fileNames.length > 1 ? "Files " : "File ") +
                    fileNames.join(', ') +
                    (fileNames.length > 1 ? " have" : " has") +
                    " invalid extension.";

                this.notificationService.show("Error!", message, NotificationType.Error);
                return;
            }
            Object.keys(this.showValidationError).forEach(key => {
                this.showValidationError[key] = false;
            });
            this.checkIfAtleastOneIsValid = true;
            if(!attachmentsToUpload || attachmentsToUpload.filter(x => x.attachmentTypeId != 15).length == 0)
            {
                //at least 1 attachment type needs to be uploaded
                this.checkIfAtleastOneIsValid = false;
                return;
            }

            Object.keys(this.showValidationError).forEach(key => {
                const docs = Enumerable.from(attachmentsToUpload).where(c => c.attachmentTypeId.toString() == key);
                if (key == "12") {
                    this.showValidationError[key] = docs.count() > 0 && docs.count(c => acceptableExcelMimeTypes.indexOf(c.file.type) != -1) < 1;
                } else if (key != "15") {
                    //show validation if has on required file type uploaded but not both
                    this.showValidationError[key] = docs.count() > 0 && (docs.count(c => acceptablePdfMimeTypes.indexOf(c.file.type) != -1) < 1 || docs.count(c => acceptableExcelMimeTypes.indexOf(c.file.type) != -1) < 1);
                }
            })
            
           let allValid = true;
            Object.keys(this.showValidationError).forEach(key => {
                if(this.showValidationError[key] && key != "15"){
                    allValid = false;
                    return;
                }
            });

            if (Object.keys(this.showValidationError).some(c => this.showValidationError[c]) && !allValid) {
                setTimeout(() => {
                    const validationElement = document.getElementsByClassName('validation').item(0);

                    if (validationElement && validationElement.parentElement) {
                        validationElement.parentElement.scrollIntoView({ behavior: "smooth" });
                    }
                });
                return;
            }

            for (const request of attachmentsToUpload) {
                ps.push(new Promise<void>((resolve, reject) => {
                    this.attachmentClientService.saveAttachments(request).then(() => {
                        this._uploadedAttachments.push(request);
                        resolve();
                    }).catch(reject);
                }));
            }

            await Promise.all(ps);
            this.attachmentRequests = [];
            this._uploadedAttachments = [];

            this.isSubmitted = true;
            this.notificationService.show(this.i18N.tr("attachment-uploaded-successfully-text"), "", NotificationType.Success);
        } catch {
            this.notificationService.show("Error!", this.i18N.tr("attachment-could-not-be-uploaded-text"), NotificationType.Error);
        }
    }

    public documentClick: EventListenerOrEventListenerObject = e => {
        if (!(<any>e).propagationStopped) {
            this.eventAggregator.publish(Messages.documentClick, { source: "document" });
        }
    };
}
